import React, { useState } from 'react';
import axios from 'axios';
import PowerOnboarding from '../AreaOverview/PowerOnboarding';
import PlayerPowerBehaviour from './PlayerPowerBehaviour';
import PowerDiscovery from './PowerDiscovery';
import ScreenStartupPlayback from './ScreenStartupPlayback';
import { profileInputDestination } from '../AreaOverview/InputAutomationTrainer';

export default function PowerMainPanel({ areaId, screen, phase, topology, devices, onTopologyChange, busy, onBusyChange, onOpenInputTraining }) {
  const players = (screen.players || []).map((player) => {
    const id = Number(player.deviceId || player.id);
    return { id, name: devices.find((device) => Number(device.id) === id)?.name || player.name || `Device ${id}` };
  });
  const [mode, setMode] = useState('edit');
  const [switchMessage, setSwitchMessage] = useState('');
  const homeTrained = (screen.activationStrategy?.automationProfiles || []).some((profile) =>
    profile.goal === 'input_selector' && profile.trainingState === 'validated'
    && profileInputDestination(profile) === 'home');
  const switchBeforeShutdown = homeTrained && screen.activationStrategy?.inputSwitching?.switchToTvBeforeShutdown !== false;
  const configuredDelay = Number(screen.activationStrategy?.inputSwitching?.shutdownSwitchDelaySeconds);
  const shutdownSwitchDelaySeconds = Number.isInteger(configuredDelay) && configuredDelay >= 1 && configuredDelay <= 15 ? configuredDelay : 2;
  const saveShutdownInput = async (changes) => {
    onBusyChange(true); setSwitchMessage('');
    try {
      const nextTopology = { ...topology, screens: topology.screens.map((entry) => Number(entry.id) === Number(screen.id)
        ? { ...entry, activationStrategy: { ...entry.activationStrategy, inputSwitching: {
          ...entry.activationStrategy?.inputSwitching, ...changes,
        } } } : entry) };
      const response = await axios.put(`/api/areas/${areaId}/topology`, nextTopology,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      onTopologyChange?.(response.data);
    } catch (error) { setSwitchMessage(error.response?.data?.message || error.message); }
    finally { onBusyChange(false); }
  };
  const save = async (lifecycleProfile) => {
    const response = await axios.put(`/api/areas/${areaId}/topology/screens/${screen.id}/device-lifecycle-profile`, { lifecycleProfile },
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
    onTopologyChange?.(response.data.topology || { ...topology, screens: topology.screens.map((entry) => Number(entry.id) === Number(screen.id)
      ? { ...entry, activationStrategy: { ...entry.activationStrategy, deviceLifecycleProfile: response.data.lifecycleProfile } } : entry) });
  };
  return <div className="power-main-panel">
    <div className="power-main-panel__modes" aria-label="Power panel view">
      <button type="button" disabled={busy} aria-pressed={mode === 'edit'} onClick={() => setMode('edit')}>Edit saved routine</button>
      <button type="button" disabled={busy} aria-pressed={mode === 'setup'} onClick={() => setMode('setup')}>Guided discovery</button>
    </div>
    <fieldset disabled={busy} className="power-main-panel__body">
      {mode === 'setup' ? <PowerDiscovery areaId={areaId} screen={screen} devices={devices} onSave={save} onBusyChange={onBusyChange} /> : <>
      <h3>{phase === 'startup' ? 'Start: TV → devices → target → Suitestream' : 'Stop: leave everything ready to start again'}</h3>
      <p>Save one routine for this Screen. You can configure it manually and run discovery again after changing device or HDMI-CEC settings.</p>
      <h4>{phase === 'startup' ? '1. Start the TV' : '1. Shut down the TV'}</h4>
      <PowerOnboarding key={phase} areaId={areaId} topology={topology} devices={devices}
        selectedScreenId={screen.id} selectedPhase={phase} selectedDeviceId={screen.televisionDeviceId}
        tvMethods compact onTopologyChange={onTopologyChange} onBusyChange={onBusyChange} />
      {phase === 'shutdown' && homeTrained && <div><label><input type="checkbox" checked={switchBeforeShutdown}
        onChange={(event) => saveShutdownInput({ switchToTvBeforeShutdown: event.target.checked })} />Switch to this TV before shutting down</label>{' '}
        <label>Delay <select aria-label="Delay before shutdown" value={shutdownSwitchDelaySeconds} disabled={!switchBeforeShutdown}
          onChange={(event) => saveShutdownInput({ shutdownSwitchDelaySeconds: Number(event.target.value) })}>
          {Array.from({ length: 15 }, (_, index) => <option key={index + 1} value={index + 1}>{index + 1} s</option>)}
        </select></label></div>}
      {switchMessage && <p role="alert">{switchMessage}</p>}
      <h4>{phase === 'startup' ? '2. Start connected devices if needed' : '2. Set connected device shutdown behaviour'}</h4>
      {!players.length && <p>No connected boxes configured for this Screen.</p>}
      {players.map((player) => <PlayerPowerBehaviour key={`${player.id}-${phase}-${JSON.stringify(screen.activationStrategy?.deviceLifecycleProfile?.devices?.[player.id])}`}
        player={player} screen={screen} phase={phase} areaId={areaId} topology={topology} devices={devices}
        onSave={save} onTopologyChange={onTopologyChange} onBusyChange={onBusyChange} />)}
      {phase === 'startup' && <ScreenStartupPlayback areaId={areaId} screen={screen} topology={topology} devices={devices}
        onTopologyChange={onTopologyChange} onOpenInputTraining={onOpenInputTraining} onBusyChange={onBusyChange} />}
      </>}
    </fieldset>
  </div>;
}
